/* eslint-disable react-hooks/rules-of-hooks */

import { LoadingConstants, PERMISSION_STATUS, USER_MANAGEMENT_ROLE } from '@zg-rentals/rental-manager-env';
import React from 'react';
import { UserManagementStateContext } from '../providers/UserManagementStateProvider';
import type { UserManagementApplicationPermissionUserStatusDto } from '@zg-rentals/ts-user-management-client';
import { UmcApplicationManagerApiHandler_getUserApplicationPermissionDetailsV2 } from '@zg-rentals/ts-user-management-client';

export const _useApplicationPermissions = () => {
  const [loadingState, setLoadingState] = React.useState<LoadingConstants>(LoadingConstants.INITIAL);
  const [error, setError] = React.useState('');
  const [users, setUsers] = React.useState<Array<UserManagementApplicationPermissionUserStatusDto>>([]);

  const getUsersWithApplicationPermissionDetails = async () => {
    const profiles: Array<UserManagementApplicationPermissionUserStatusDto> = [];
    let hasMoreData = true;
    let zuid: string | undefined = undefined;
    try {
      while (hasMoreData) {
        const response = await UmcApplicationManagerApiHandler_getUserApplicationPermissionDetailsV2({ zuid });
        hasMoreData = response.hasMoreData;
        zuid =
          response.applicationPermissionUserStatuses[response.applicationPermissionUserStatuses.length - 1].userToken;
        profiles.push(...response.applicationPermissionUserStatuses);
      }
    } catch (error) {
      setLoadingState(LoadingConstants.ERROR);
      setUsers([]);
      return;
    }
    setUsers(profiles);
    setLoadingState(LoadingConstants.SUCCESS);
    return profiles;
  };

  const pendingApplicationRequestUsers = React.useMemo(() => {
    return users.filter((user) => user.permissionStatus === PERMISSION_STATUS.pending);
  }, [users]);

  const applicationManager = React.useMemo(() => {
    return users.find((user) => user.userManagementRoleString === USER_MANAGEMENT_ROLE.applicationManager);
  }, [users]);

  const companyAdmins = React.useMemo(() => {
    return users.filter((user) => user.userManagementRoleString === USER_MANAGEMENT_ROLE.companyAdmin);
  }, [users]);

  return {
    loadingState,
    users,
    getUsersWithApplicationPermissionDetails,
    pendingApplicationRequestUsers,
    applicationManager,
    companyAdmins,
    error,
  };
};

export const useApplicationPermissions = () => {
  const umcState = React.useContext(UserManagementStateContext);

  if (!umcState) throw new Error('State Not initialized');

  return React.useMemo(() => umcState.applicationState, [umcState.applicationState]);
};
